import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { AuthModal } from "@/components/ui/auth-modal";
import { CreditsModal } from "@/components/ui/credits-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { CREDIT_COSTS, getModelOption, getGenerationCreditCost, INTEGRATED_MODEL_ID, INTEGRATED_DURATION_MIN, INTEGRATED_DURATION_MAX, type ModelId } from "@/config/models";
import { api, buildQueryString } from "@/lib/api";
import { extractLastFrameAsBase64 } from "@/lib/video-utils";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { cn } from "@/lib/utils";
import { isExpired } from "@/config/retention";
import {
  Loader2,
  Sparkles,
  Star,
  Lock,
  ArrowLeft,
  FastForward,
  Film,
} from "lucide-react";
import { trackViewContent } from "@/services/analytics";
import type { GenerateRequest, Video } from "@/types";

export function ExtendVideoPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectVideoId = searchParams.get("video_id");
  const { user, isSignedIn } = useUser();
  const { credits, hasPurchased, isLoading: isUserLoading } = useUserStore();
  const generateMutation = useGenerate();

  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [lastFrame, setLastFrame] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [showAuth, setShowAuth] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [preselectApplied, setPreselectApplied] = useState(false);

  useEffect(() => {
    trackViewContent("Extend Video", user?.id, { funnel_stage: "create" });
  }, []);

  const { data, isLoading: videosLoading } = useQuery({
    queryKey: ["videos", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: Video[] }>(
        `/videos${buildQueryString({ user_id: user.id })}`,
      );
    },
    enabled: !!user?.id && hasPurchased,
  });

  // Expired videos have no file left to take the last frame from.
  const completedVideos = (data?.videos ?? []).filter(
    (v) => v.status === "completed" && v.media_type === "VIDEO" && v.url && !isExpired(v.created_at),
  );

  const handleSelectVideo = useCallback(async (video: Video) => {
    setSelectedVideo(video);
    setExtractError(null);
    setExtracting(true);
    try {
      const frame = await extractLastFrameAsBase64(video.url!);
      setLastFrame(frame);
    } catch {
      setExtractError("Failed to extract the last frame. Please try another video.");
      setSelectedVideo(null);
    } finally {
      setExtracting(false);
    }
  }, []);

  // Auto-select a preselected video from the ?video_id= param (deep-link from
  // the gen success page). Runs once when the videos list is available.
  useEffect(() => {
    if (preselectApplied || !preselectVideoId || !hasPurchased) return;
    const match = completedVideos.find((v) => v.id === preselectVideoId);
    if (match) {
      setPreselectApplied(true);
      handleSelectVideo(match);
      setSearchParams({}, { replace: true });
    }
  }, [
    preselectVideoId,
    preselectApplied,
    hasPurchased,
    completedVideos,
    handleSelectVideo,
    setSearchParams,
  ]);

  const handleBack = useCallback(() => {
    setSelectedVideo(null);
    setLastFrame(null);
    setPrompt("");
    setExtractError(null);
  }, []);

  const selectedSegmentDurationMs =
    selectedVideo?.segment_duration_ms ?? selectedVideo?.duration_ms;
  const extendSeconds =
    selectedVideo?.model_id === INTEGRATED_MODEL_ID
      ? Math.min(
          INTEGRATED_DURATION_MAX,
          Math.max(
            INTEGRATED_DURATION_MIN,
            selectedSegmentDurationMs
              ? Math.round(selectedSegmentDurationMs / 1000)
              : INTEGRATED_DURATION_MIN,
          ),
        )
      : undefined;

  const selectedModelOption = selectedVideo
    ? getModelOption(selectedVideo.model_id as ModelId)
    : undefined;
  const baseCost = selectedModelOption
    ? getGenerationCreditCost(selectedModelOption, extendSeconds)
    : selectedVideo
      ? (CREDIT_COSTS[selectedVideo.model_id as ModelId] ?? selectedVideo.credits_used)
      : 0;
  const displayCost = baseCost;

  const canGenerate = prompt.trim().length > 0 && lastFrame && selectedVideo;

  const handleGenerate = useCallback(async () => {
    if (!canGenerate || !selectedVideo || !lastFrame || isUserLoading) return;

    if (credits < displayCost) {
      setShowCreditsModal(true);
      return;
    }

    try {
      const params: Omit<GenerateRequest, "user_id"> = {
        model_id: selectedVideo.model_id,
        prompt: prompt.trim(),
        source_video_id: selectedVideo.id,
      };

      if (selectedVideo.model_id === INTEGRATED_MODEL_ID && extendSeconds) {
        params.duration_seconds = extendSeconds;
      }

      const result = await generateMutation.mutateAsync(params);
      if (result.video?.id) {
        navigate(`/generation/${result.video.id}`);
      }
    } catch (err) {
      console.error("Extend generation failed:", err);
    }
  }, [canGenerate, selectedVideo, lastFrame, prompt, credits, displayCost, extendSeconds, generateMutation, navigate, isUserLoading]);

  return (
    <div className="max-w-xl mx-auto px-5 md:px-8 pt-6 pb-16">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          Extend Video
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Continue a video from where it left off. Select one of your videos and describe what happens next.
        </p>
      </div>

      {!hasPurchased ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="size-16 rounded-3xl bg-orange-50 border border-orange-200/60 flex items-center justify-center mb-4">
            <Lock className="size-7 text-[#e8822e]" strokeWidth={1.5} />
          </div>
          <h3 className="text-base font-bold text-foreground mb-1.5">
            Unlock Extend Video
          </h3>
          <p className="text-sm text-muted-foreground mb-6 text-center max-w-xs">
            Purchase any star pack to extend your videos. You'll also get watermark-free downloads.
          </p>
          <Link
            to="/billing"
            className="btn-primary text-sm px-6 py-2.5 flex items-center gap-2"
          >
            <Star className="size-4" />
            Get Stars
          </Link>
        </div>
      ) : selectedVideo && lastFrame ? (
        <div className="space-y-4 animate-fade-in-up">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors mb-2"
          >
            <ArrowLeft className="size-3.5" />
            Choose different video
          </button>

          <div className="rounded-2xl overflow-hidden border-2 border-[rgba(45,49,39,0.08)] bg-[#f0ebe0]">
            <img
              src={lastFrame}
              alt="Last frame"
              className="w-full max-h-[300px] object-contain"
            />
          </div>

          <div className="rounded-xl bg-[rgba(45,49,39,0.02)] border border-[rgba(45,49,39,0.06)] px-3.5 py-2.5">
            <p className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-1">
              Original Prompt
            </p>
            <p className="text-[12px] text-muted-foreground leading-relaxed">
              {selectedVideo.prompt}
            </p>
          </div>

          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe what happens next... e.g. character turns around, dramatic wind blows, camera zooms out to reveal the city..."
            rows={3}
            className="input-field resize-none"
          />

          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <FastForward className="size-3.5" />
              <span>Same model as original</span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Star className="size-3" />
              <span>{displayCost} stars</span>
            </div>
          </div>

          <div className="hidden md:block">
            <ExtendButtons
              isSignedIn={!!isSignedIn}
              isUserLoading={isUserLoading}
              canGenerate={!!canGenerate}
              isPending={generateMutation.isPending}
              creditCost={displayCost}
              onGenerate={handleGenerate}
              onShowAuth={() => setShowAuth(true)}
            />
          </div>

          <div className="h-20 md:hidden" aria-hidden="true" />

          {createPortal(
            <div className="md:hidden fixed bottom-0 inset-x-0 z-50">
              <div className="px-5 pt-3 pb-3 bg-white/95 backdrop-blur-xl border-t border-[rgba(45,49,39,0.06)]">
                <ExtendButtons
                  isSignedIn={!!isSignedIn}
                  isUserLoading={isUserLoading}
                  canGenerate={!!canGenerate}
                  isPending={generateMutation.isPending}
                  creditCost={displayCost}
                  onGenerate={handleGenerate}
                  onShowAuth={() => setShowAuth(true)}
                />
              </div>
              <div
                className="bg-white/95 backdrop-blur-xl"
                style={{ height: "env(safe-area-inset-bottom, 0px)" }}
              />
            </div>,
            document.body,
          )}

          {generateMutation.isError && (
            <NsfwOrError error={generateMutation.error} />
          )}
        </div>
      ) : (
        <div className="animate-fade-in-up">
          {extracting && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="size-6 animate-spin text-[#e8822e] mb-3" />
              <p className="text-sm text-muted-foreground">
                Extracting last frame...
              </p>
            </div>
          )}

          {extractError && (
            <div className="rounded-lg bg-red-50 border-2 border-red-200 p-3 mb-4">
              <p className="text-[12px] text-red-600">{extractError}</p>
            </div>
          )}

          {!extracting && videosLoading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="size-6 animate-spin text-[#e8822e]" />
            </div>
          )}

          {!extracting && !videosLoading && completedVideos.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="size-16 rounded-3xl bg-[rgba(45,49,39,0.04)] border border-[rgba(45,49,39,0.06)] flex items-center justify-center mb-4">
                <Film className="size-7 text-[#e8822e]" strokeWidth={1.5} />
              </div>
              <h3 className="text-base font-bold text-foreground mb-1.5">
                No videos to extend
              </h3>
              <p className="text-sm text-muted-foreground mb-6 text-center max-w-xs">
                Create an animation first, then come back here to extend it.
              </p>
              <Link
                to="/create/image-to-video"
            className="btn-primary text-sm px-6 py-2.5"
          >
            Create Animation
              </Link>
            </div>
          )}

          {!extracting && completedVideos.length > 0 && (
            <>
              <p className="text-[13px] font-semibold text-muted-foreground mb-3">
                Choose a video to extend
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                {completedVideos.map((video) => (
                  <button
                    key={video.id}
                    onClick={() => handleSelectVideo(video)}
                    className="group rounded-2xl border-2 border-[rgba(45,49,39,0.06)] bg-white overflow-hidden hover:border-orange-300/60 transition-all cursor-pointer text-left"
                  >
                    <div className="relative w-full aspect-square bg-[#f0ebe0] overflow-hidden">
                      <video
                        src={`${video.url!}#t=0.1`}
                        className="w-full h-full object-contain"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        onPointerEnter={(e) =>
                          e.currentTarget.play().catch(() => {})
                        }
                        onPointerLeave={(e) => {
                          e.currentTarget.pause();
                          e.currentTarget.currentTime = 0;
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                        <div className="size-10 rounded-full bg-orange-50/80 backdrop-blur-sm border border-orange-200/60 flex items-center justify-center">
                          <FastForward className="size-4 text-[#e8822e]" />
                        </div>
                      </div>
                    </div>
                    <div className="px-2.5 py-2">
                      <p className="text-[10px] sm:text-[11px] text-muted-foreground truncate mb-1">
                        {video.prompt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-muted-foreground">
                          {video.credits_used} ★
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
      />

      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        creditCost={displayCost}
        credits={credits}
      />
    </div>
  );
}

function ExtendButtons({
  isSignedIn,
  isUserLoading,
  canGenerate,
  isPending,
  creditCost,
  onGenerate,
  onShowAuth,
}: {
  isSignedIn: boolean;
  isUserLoading: boolean;
  canGenerate: boolean;
  isPending: boolean;
  creditCost: number;
  onGenerate: () => void;
  onShowAuth: () => void;
}) {
  if (!isSignedIn) {
    return (
      <div>
        <button
          onClick={onShowAuth}
          className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2"
        >
          <Sparkles className="size-4" />
          Extend Video
        </button>
        <p className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground/50 mt-1.5">
          <Star className="size-2.5" />
          {creditCost} stars
        </p>
      </div>
    );
  }

  return (
    <div>
      {isUserLoading ? (
        <div className="w-full h-[46px] rounded-lg bg-orange-100/60 animate-pulse" />
      ) : (
        <button
          onClick={onGenerate}
          disabled={!canGenerate || isPending}
          className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2"
        >
          {isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Extending...
            </>
          ) : (
            <>
              <Sparkles className="size-4" />
              Extend Video
            </>
          )}
        </button>
      )}
      <p className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground/50 mt-1.5">
        <Star className="size-2.5" />
        {creditCost} stars
      </p>
    </div>
  );
}
